import React, { useEffect, useRef, useState } from 'react';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';

function useGetFilterByURL<T>(initialValue: T, skipOffset: boolean) {
  const { search } = useLocation();
  if (!search) {
    return initialValue;
  }
  const params = queryString.parse(search, { arrayFormat: 'bracket' });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const state: any = { ...initialValue };
  for (const key in initialValue) {
    if (params[key] !== undefined) {
      state[key] = params[key];
    }
  }

  if (skipOffset) {
    return {
      ...params,
      offset: 0,
      nextPageToken: undefined,
    };
  }
  return params;
}

export function useSaveUrlFilter<T>(
  initialValue: T,
  skipOffset = false,
): [T, (value: React.SetStateAction<T>) => void] {
  const navigate = useNavigate();
  const { state: locationState, search: locationSearch } = useLocation();
  const init = useGetFilterByURL<T>(initialValue, skipOffset);
  const [state, setState] = useState<T>({ ...init } as T);

  const isMounted = useRef(false);
  const prevState = useRef(state);

  useEffect(() => {
    if (isMounted.current && prevState.current === state) {
      return;
    }

    isMounted.current = true;
    prevState.current = state;
    const search = queryString.stringify(
      {
        ...queryString.parse(locationSearch, { arrayFormat: 'bracket' }),
        ...state,
      } as Record<string, string | object | null>,
      { arrayFormat: 'bracket' },
    );
    navigate({ search }, { replace: true, state: locationState });
  }, [locationSearch, locationState, navigate, state]);

  return [state, setState];
}
